/**
 * Project a `/sources/links` response into a safe onboarding error.
 *
 * The endpoint returns a JSON import session even when the provider could not
 * read a link. This policy keeps that provider result from being mistaken for
 * usable evidence by the discovery/style stages.
 */

const SOURCE_IMPORT_FAILURE_MESSAGE =
	'Не получилось прочитать Telegram по этой ссылке. Проверьте, что канал публичный, или вставьте пару своих постов/короткое описание бизнеса.';

const FAILURE_STATUSES = new Set([
	'failed',
	'error',
	'unavailable',
	'unsupported',
	'authorization_required',
	'fallback'
]);

type JsonRecord = Record<string, unknown>;

const parseJsonValue = (value: unknown): unknown => {
	if (typeof value !== 'string') return value;
	const text = value.trim();
	if (!text) return value;
	try {
		return JSON.parse(text) as unknown;
	} catch {
		return value;
	}
};

const asItems = (value: unknown): unknown[] => {
	const parsed = parseJsonValue(value);
	if (Array.isArray(parsed)) return parsed;
	if (parsed && typeof parsed === 'object') return [parsed];
	return [];
};

const hasFailureStatus = (value: unknown): boolean =>
	asItems(value).some((item) => {
		if (!item || typeof item !== 'object') return false;
		const status = String((item as JsonRecord).status ?? '')
			.trim()
			.toLowerCase();
		return FAILURE_STATUSES.has(status);
	});

const hasNonEmptyError = (value: unknown): boolean => {
	const parsed = parseJsonValue(value);
	if (parsed === null || parsed === undefined) return false;
	if (typeof parsed === 'string') return parsed.trim().length > 0;
	if (Array.isArray(parsed)) return parsed.length > 0;
	if (typeof parsed === 'object') return Object.keys(parsed as JsonRecord).length > 0;
	return true;
};

export class Onboarding1SourceImportError extends Error {
	readonly code = 'source_import_failed' as const;

	constructor() {
		super(SOURCE_IMPORT_FAILURE_MESSAGE);
		this.name = 'Onboarding1SourceImportError';
	}
}

/** Throw before text/style/discovery when a link import contains failure evidence. */
export const assertOnboarding1LinkImportSucceeded = (result: JsonRecord): void => {
	if (!result || typeof result !== 'object' || Array.isArray(result)) {
		throw new Onboarding1SourceImportError();
	}
	const status = String(result.status ?? '')
		.trim()
		.toLowerCase();
	const providerRefsFailed = [result.provider_refs, result.provider_refs_json].some(
		hasFailureStatus
	);
	const onApi = parseJsonValue(result.on_api);
	const onApiItems = onApi && typeof onApi === 'object' ? (onApi as JsonRecord).items : undefined;

	if (
		status === 'failed' ||
		status === 'error' ||
		hasNonEmptyError(result.error_json) ||
		providerRefsFailed ||
		hasFailureStatus(onApiItems)
	) {
		throw new Onboarding1SourceImportError();
	}
};
